import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { ANUploader } from "@answersnow/pkg-theme";

import { space3, space5 } from "../../styled";
import type { AssessmentRecord, VbmappMilestonesFormData } from "../../types";
import { AssessmentDatePicker, FormActionRow, ReadOnlyRow, ScoreInputRow } from "./shared";
import { getUser } from "selectors";

interface VbmappMilestonesDataEntryProps {
  administratorName: string;
  previousAssessment: AssessmentRecord | null;
  onCancel: () => void;
  onSave: (data: VbmappMilestonesFormData) => void;
}

type ScoreKey = keyof Omit<VbmappMilestonesFormData, "administrationDate" | "uploadedFiles">;

interface ScoreField {
  key: ScoreKey;
  label: string;
  max: number;
}

const SCORE_FIELDS: ScoreField[] = [
  { key: "mand", label: "Mand", max: 15 },
  { key: "tact", label: "Tact", max: 15 },
  { key: "listenerResponding", label: "Listener Responding", max: 15 },
  { key: "visualPerceptualMts", label: "Visual Perceptual Skills and Matching-to-Sample", max: 15 },
  { key: "play", label: "Play", max: 15 },
  { key: "social", label: "Social", max: 15 },
  { key: "motorImitation", label: "Motor Imitation", max: 10 },
  { key: "echoic", label: "Echoic", max: 10 },
  { key: "vocal", label: "Vocal", max: 5 },
  {
    key: "listenerRespondingLrffc",
    label: "Listener Responding by Feature, Function, Class",
    max: 10,
  },
  { key: "intraverbal", label: "Intraverbal", max: 10 },
  { key: "group", label: "Group & Classroom Skills", max: 10 },
  { key: "linguistics", label: "Linguistics", max: 10 },
  { key: "reading", label: "Reading", max: 5 },
  { key: "writing", label: "Writing", max: 5 },
  { key: "math", label: "Math", max: 5 },
];

/** Reads a numeric score from an AssessmentRecord's snake_case data. */
function readPrevScore(prev: AssessmentRecord | null, snakeKey: string): number | null {
  if (!prev) return null;
  const val = prev.data[snakeKey];
  return typeof val === "number" ? val : null;
}

function buildInitialForm(previous: AssessmentRecord | null): VbmappMilestonesFormData {
  return {
    administrationDate: null,
    administratorName: "",
    mand: readPrevScore(previous, "mand"),
    tact: readPrevScore(previous, "tact"),
    listenerResponding: readPrevScore(previous, "listener_responding"),
    visualPerceptualMts: readPrevScore(previous, "visual_perceptual_mts"),
    play: readPrevScore(previous, "play"),
    social: readPrevScore(previous, "social"),
    motorImitation: readPrevScore(previous, "motor_imitation"),
    echoic: readPrevScore(previous, "echoic"),
    vocal: readPrevScore(previous, "vocal"),
    listenerRespondingLrffc: readPrevScore(previous, "listener_responding_lrffc"),
    intraverbal: readPrevScore(previous, "intraverbal"),
    group: readPrevScore(previous, "group"),
    linguistics: readPrevScore(previous, "linguistics"),
    reading: readPrevScore(previous, "reading"),
    writing: readPrevScore(previous, "writing"),
    math: readPrevScore(previous, "math"),
    uploadedFiles: [],
  };
}

/**
 * Data entry form for VB-MAPP Milestones assessment.
 * Collects section scores and optional milestones grid file upload.
 * Total Score is auto-calculated and read-only; not sent to the backend.
 */
export default function VbmappMilestonesDataEntry({
  administratorName,
  previousAssessment,
  onCancel,
  onSave,
}: VbmappMilestonesDataEntryProps) {
  const currentUserName = (useSelector(getUser) as { name?: string })?.name ?? "";
  const [form, setForm] = useState<VbmappMilestonesFormData>(() =>
    buildInitialForm(previousAssessment)
  );
  const [localAdminName, setLocalAdminName] = useState<string>(
    currentUserName || administratorName
  );

  // Sync Redux user name to state whenever it changes (for auto-population)
  useEffect(() => {
    if (currentUserName) {
      setLocalAdminName(currentUserName);
    }
  }, [currentUserName]);

  const totalScore = useMemo(() => {
    const entered = SCORE_FIELDS.map((f) => form[f.key]).filter((v): v is number => v !== null);
    return entered.length > 0 ? entered.reduce((a, b) => a + b, 0) : null;
  }, [form]);

  function handleScoreChange(key: ScoreKey, value: string) {
    const parsed = value === "" ? null : Number(value);
    setForm((prev) => ({ ...prev, [key]: parsed }));
  }

  function handleFileUpload({
    acceptedFiles,
    fileRejections,
  }: {
    acceptedFiles: File[];
    fileRejections: any[];
  }) {
    const wrappers = [
      ...acceptedFiles.map((file) => ({ file, errors: false })),
      ...fileRejections.map((f) => ({ file: f.file as File, errors: true })),
    ];
    setForm((prev) => ({ ...prev, uploadedFiles: [...prev.uploadedFiles, ...wrappers] }));
  }

  function handleFileDelete(wrapper: { file: File; errors: boolean }) {
    setForm((prev) => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter((f) => f !== wrapper),
    }));
  }

  return (
    <Stack component="form" sx={{ height: "100%" }} noValidate>
      <Stack sx={{ overflowY: "auto", flex: 1, pr: 1 }} rowGap={space5}>
        {/* Administrator Name (auto-populated but editable) */}
        <TextField
          label="Administrator Name"
          size="small"
          value={localAdminName}
          onChange={(e) => setLocalAdminName(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        {/* Administration date — DatePicker with fallback to manual typing */}
        <AssessmentDatePicker
          value={form.administrationDate}
          onChange={(date) => setForm((prev) => ({ ...prev, administrationDate: date }))}
        />

        {/* Info alert */}
        <Alert
          severity="info"
          sx={{
            borderRadius: "16px",
            backgroundColor: "#E5F6FD",
            "& .MuiAlert-icon": { alignItems: "center" },
          }}
        >
          Leaving a score as empty will report the score as &quot;Not Tested&quot;
        </Alert>

        {/* Score fields */}
        <Stack rowGap={space3}>
          {SCORE_FIELDS.map((field) => (
            <ScoreInputRow
              key={field.key}
              label={field.label}
              value={form[field.key]}
              max={field.max}
              step={0.5}
              showMax={false}
              onChange={(value) => handleScoreChange(field.key, value)}
            />
          ))}

          {/* Auto-calculated Total Score */}
          <ReadOnlyRow label="Total Score" value={totalScore} />
        </Stack>

        {/* Milestones grid file upload */}
        <Stack rowGap={space3}>
          <Typography variant="body2" color="text.secondary">
            Milestones Grid (optional)
          </Typography>
          <Box>
            <ANUploader
              dropzoneFiles={form.uploadedFiles}
              alreadyUploadedFiles={[]}
              handleFileUpload={handleFileUpload}
              handleFileDelete={handleFileDelete}
              handleLoadedFileDelete={() => {}}
              maxFileSize={3 * 1024 * 1024}
              useDropzoneOptions={{
                accept: {
                  "image/png": [],
                  "image/jpeg": [],
                  "application/pdf": [],
                },
                multiple: false,
              }}
            />
          </Box>
        </Stack>
      </Stack>
      <Box sx={{ pt: space3, flexShrink: 0 }}>
        <FormActionRow
          onCancel={onCancel}
          onSubmit={() => onSave({ ...form, administratorName: localAdminName })}
          submitLabel="Add Assessment"
        />
      </Box>
    </Stack>
  );
}
